'use client';

import { X } from '@phosphor-icons/react/ssr';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Separator } from '../ui/separator';
import { IconButton } from './icon-button';
import { focusBodyFirst } from './overlay-focus';
import { drawerPosition, drawerStyles } from './overlay-styles';

export type DrawerSide = 'right' | 'left';
export type DrawerSize = 'default' | 'wide';

type SectionProps = {
  /** Figma "Side": Right (default) for details / inspectors; Left for navigation or filters on mobile. */
  side?: DrawerSide;
  /** Figma "Size": Default 400 · Wide 560. */
  size?: DrawerSize;
  /** Figma "Title" - names the drawer. */
  title: ReactNode;
  /** Figma "Description" (Show description). */
  description?: ReactNode;
  /** Close button in the header. On by default. */
  showClose?: boolean;
  /** Accessible name of the Close button. */
  closeLabel?: string;
  /** Figma "Footer": actions, right-aligned (Ghost Cancel as `DrawerClose asChild`, then Primary). */
  footer?: ReactNode;
  /** Left side of the footer (Figma "Spacer"), e.g. a destructive link. */
  footerStart?: ReactNode;
  /** Figma "Content": the body - fills the height and scrolls; header and footer stay pinned. */
  children?: ReactNode;
};

function DrawerSections({
  side,
  size,
  title,
  description,
  showClose = true,
  closeLabel = 'Close',
  footer,
  footerStart,
  children,
}: SectionProps) {
  const styles = drawerStyles({ side, size });
  return (
    <>
      <div data-slot="drawer-header" className={styles.header()}>
        <div data-slot="drawer-titles" className={styles.titles()}>
          <DialogTitle data-slot="drawer-title" className={styles.title()}>
            {title}
          </DialogTitle>
          {description !== undefined && (
            <DialogDescription
              data-slot="drawer-description"
              className={styles.description()}
            >
              {description}
            </DialogDescription>
          )}
        </div>
        {showClose && (
          <DialogClose asChild>
            <IconButton
              data-slot="drawer-close"
              icon={X}
              size="sm"
              aria-label={closeLabel}
            />
          </DialogClose>
        )}
      </div>
      <Separator data-slot="drawer-divider" className={styles.divider()} />
      <div data-slot="drawer-body" className={styles.body()}>
        {children}
      </div>
      {(footer !== undefined || footerStart !== undefined) && (
        <div data-slot="drawer-footer" className={styles.footer()}>
          <div data-slot="drawer-footer-start" className={styles.footerStart()}>
            {footerStart}
          </div>
          {footer}
        </div>
      )}
    </>
  );
}

export type DrawerContentProps = Omit<ComponentProps<typeof DialogContent>, 'title'> &
  SectionProps;

/**
 * The Figma Drawer: a full-height sheet pinned to the right (or left) edge.
 * Modal by default - on the bg/overlay backdrop, focus trapped and returned
 * to the trigger, Esc / Close / a backdrop click dismiss it. Initial focus
 * goes to the body's first control, else Close.
 */
export function DrawerContent({
  side,
  size,
  title,
  description,
  showClose,
  closeLabel,
  footer,
  footerStart,
  children,
  className,
  onOpenAutoFocus,
  ...props
}: DrawerContentProps) {
  return (
    <DialogContent
      data-side={side ?? 'right'}
      {...(description === undefined ? { 'aria-describedby': undefined } : {})}
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event);
        if (!event.defaultPrevented) focusBodyFirst(event, 'drawer-body');
      }}
      className={drawerStyles({ side, size }).root({
        className: drawerPosition({ side, className }),
      })}
      {...props}
    >
      <DrawerSections
        side={side}
        size={size}
        title={title}
        description={description}
        showClose={showClose}
        closeLabel={closeLabel}
        footer={footer}
        footerStart={footerStart}
      >
        {children}
      </DrawerSections>
    </DialogContent>
  );
}

export type DrawerPanelProps = SectionProps & { className?: string };

/**
 * The drawer drawn in place (docs, the Figma frame): same classes as
 * `DrawerContent`, without the portal, backdrop or focus trap. Give it a height.
 */
export function DrawerPanel({ side, size, className, ...sections }: DrawerPanelProps) {
  return (
    <DialogPrimitive.Root>
      <div
        data-slot="drawer-content"
        data-side={side ?? 'right'}
        className={drawerStyles({ side, size }).root({ className })}
      >
        <DrawerSections side={side} size={size} {...sections} />
      </div>
    </DialogPrimitive.Root>
  );
}

/**
 * Full-height side sheet for detail views and longer edits that keep the
 * page in context (Figma: Components ↳ Drawer). Compose `Drawer` ›
 * `DrawerTrigger asChild` › `DrawerContent`. Prefer it over a Modal for long
 * content or editing several items in a row. `modal={false}` drops the
 * backdrop and focus trap (a docked inspector - lay the page out beside it).
 */
export function Drawer(props: ComponentProps<typeof Dialog>) {
  return <Dialog {...props} />;
}

export { DialogClose as DrawerClose, DialogTrigger as DrawerTrigger };
